import { useEffect, useRef, useState } from 'react';
import ChatBubble from './ChatBubble';
import QuickReplies from './QuickReplies';
import { getPropertyById } from './PropertySelector';
import { sendChatMessage } from './demoApi';

const WELCOME_MESSAGES = {
  en: "Welcome! I'm your Valamar AI concierge. Ask me about check-in, dining, spa, activities, or anything about your stay.",
  hr: 'Dobrodošli! Ja sam vaš Valamar AI concierge. Pitajte me o check-inu, restoranima, spa-u, aktivnostima ili boravku.',
  de: 'Willkommen! Ich bin Ihr Valamar AI Concierge. Fragen Sie mich zu Check-in, Restaurants, Spa, Aktivitäten oder Ihrem Aufenthalt.',
  it: 'Benvenuto! Sono il vostro concierge AI Valamar. Chiedetemi informazioni su check-in, ristoranti, spa, attività o il soggiorno.',
};

export default function ChatWindow({
  propertyId,
  language,
  guestContext,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const property = getPropertyById(propertyId);

  useEffect(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: WELCOME_MESSAGES[language] || WELCOME_MESSAGES.en,
        timestamp: new Date(),
      },
    ]);
    setError(null);
  }, [propertyId, language]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function sendMessage(text) {
    const trimmed = text?.trim();
    if (!trimmed || loading) return;

    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);
    setError(null);

    const history = [...messages, userMsg]
      .filter((m) => m.id !== 'welcome')
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const data = await sendChatMessage({
        message: trimmed,
        history: history.slice(0, -1),
        property: propertyId,
        language,
        guestContext,
      });

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.message,
          cta: data.cta,
          timestamp: new Date(),
        },
      ]);
    } catch (err) {
      setError(err.message || 'Failed to get response');
    } finally {
      setLoading(false);
    }
  }

  function handleCtaClick(cta) {
    const actionMessages = {
      spa_booking: "Yes, I'd like to book the spa treatment.",
      dining_booking: "Yes, please book a table for tonight.",
      late_checkout: 'Yes, I would like late checkout please.',
      transfer: 'Yes, please arrange a transfer for me.',
      upgrade: "Yes, I'm interested in the room upgrade.",
      extend_stay: "Yes, I'd like to extend my stay.",
      other: "Yes, I'm interested in this offer.",
    };
    sendMessage(actionMessages[cta.action] || actionMessages.other);
  }

  function handleSubmit(e) {
    e.preventDefault();
    sendMessage(input);
  }

  return (
    <div className="demo-chat-col">
      <div className="chat-property-banner">
        <div
          className="chat-property-accent"
          style={{ background: property.accent }}
        />
        <div className="chat-property-info">
          <h3>
            {property.name}
            <span className="demo-badge">DEMO</span>
          </h3>
          <p>{property.tagline} · {'★'.repeat(property.stars)}</p>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 1 && (
          <div className="chat-welcome">
            Adjust settings in the Systems panel, then ask a question.
            Try changing loyalty tier from None to Gold and ask the same thing twice.
          </div>
        )}
        {messages.map((msg) => (
          <ChatBubble
            key={msg.id}
            message={msg}
            onCtaClick={handleCtaClick}
          />
        ))}
        {loading && (
          <div className="chat-bubble-row assistant">
            <div className="chat-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {error && <div className="chat-error">{error}</div>}

      <QuickReplies onSelect={sendMessage} disabled={loading} />

      <form className="chat-input-row" onSubmit={handleSubmit}>
        <input
          className="chat-input"
          type="text"
          placeholder="Type a message…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          className="chat-send-btn"
          disabled={loading || !input.trim()}
          aria-label="Send"
        >
          ➤
        </button>
      </form>
    </div>
  );
}
